import { useState, useEffect, useCallback, useRef } from 'react';

interface UseWakeWordOptions {
  enabled: boolean;
  onWakeWordDetected: (remainingText?: string) => void;
  onError?: (error: Error) => void;
}

interface UseWakeWordReturn {
  isListening: boolean;
  isInitialized: boolean;
  error: Error | null;
  start: () => Promise<void>;
  stop: () => Promise<void>;
}

// Wake word is always "komplic" with various phonetic alternatives
const getWakeWord = (): string => {
  return 'komplic';
};

export const useWakeWord = ({ enabled, onWakeWordDetected, onError }: UseWakeWordOptions): UseWakeWordReturn => {
  const [isListening, setIsListening] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const recognitionRef = useRef<any>(null);
  const lastDetectionTimeRef = useRef<number>(0);
  const enabledRef = useRef<boolean>(enabled);

  // Initialize Speech Recognition
  useEffect(() => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      const err = new Error('Speech Recognition not supported in this browser');
      setError(err);
      if (onError) onError(err);
      return;
    }

    try {
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'cs-CZ'; // Czech language for "hej"
      recognition.maxAlternatives = 3;

      recognition.onstart = () => {
        setIsListening(true);
      };

      recognition.onend = () => {
        setIsListening(false);

        // Auto-restart if still enabled (use ref to get current value)
        if (enabledRef.current && recognitionRef.current) {
          try {
            recognition.start();
          } catch (err) {
            // Already running or restarting
          }
        }
      };

      recognition.onerror = (event: any) => {
        // Silently handle errors

        // Ignore "no-speech" errors (common when listening continuously)
        if (event.error === 'no-speech') return;

        setError(new Error(event.error));
      };

      recognition.onresult = (event: any) => {
        const results = event.results;
        const lastResult = results[results.length - 1];

        if (!lastResult) return;

        // Check all alternatives
        for (let i = 0; i < lastResult.length; i++) {
          const transcript = lastResult[i].transcript.toLowerCase().trim();
          const confidence = lastResult[i].confidence;

          // Check if wake word is detected with phonetic variations
          const wakeWord = getWakeWord().toLowerCase();

          // Normalize and fuzzy match for "komplic" phonetic variations
          const normalizeForMatching = (text: string) => {
            return text
              // All komplic/complic variations
              .replace(/komplic/gi, 'komplic')
              .replace(/complic/gi, 'komplic')
              .replace(/compact/gi, 'komplic')
              .replace(/compic/gi, 'komplic')
              .replace(/konklic/gi, 'komplic')
              .replace(/konplic/gi, 'komplic')
              .replace(/komplik/gi, 'komplic')
              // Complete/completes variations
              .replace(/complete[sd]?/gi, 'komplic')
              .replace(/complitz/gi, 'komplic');
          };

          const normalizedTranscript = normalizeForMatching(transcript);

          const detected = normalizedTranscript === wakeWord ||
                          normalizedTranscript.startsWith(wakeWord + ' ') ||
                          normalizedTranscript.includes(' ' + wakeWord + ' ') ||
                          normalizedTranscript.includes(' ' + wakeWord) ||
                          normalizedTranscript.endsWith(' ' + wakeWord);

          if (detected && confidence > 0.5) {
            // Debounce - prevent multiple triggers
            const now = Date.now();
            if (now - lastDetectionTimeRef.current < 2000) {
              return; // 2s cooldown
            }

            lastDetectionTimeRef.current = now;

            // Extract remaining text after wake word
            let remainingText = '';

            // Try to find any of the wake word variations in the original transcript
            const variations = ['komplic', 'complic', 'compact', 'compic', 'konklic', 'konplic', 'komplik'];

            for (const variant of variations) {
              const variantIndex = transcript.indexOf(variant);
              if (variantIndex !== -1) {
                remainingText = transcript.substring(variantIndex + variant.length).trim();
                break;
              }
            }

            onWakeWordDetected(remainingText || undefined);
            break;
          }
        }
      };

      recognitionRef.current = recognition;
      setIsInitialized(true);
      setError(null);

    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to initialize');
      setError(error);
      if (onError) onError(error);
    }

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (err) {
          console.log('Recognition already stopped');
        }
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Start listening
  const start = useCallback(async () => {
    if (!recognitionRef.current || !isInitialized) {
      return;
    }

    try {
      recognitionRef.current.start();
    } catch (err: any) {
      if (err.message && err.message.includes('already started')) {
        setIsListening(true);
      }
    }
  }, [isInitialized]);

  // Stop listening
  const stop = useCallback(async () => {
    if (!recognitionRef.current) return;

    try {
      recognitionRef.current.stop();
    } catch (err) {
      // Failed to stop
    }
  }, []);

  // Update enabled ref when prop changes
  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  // Auto-start/stop based on enabled prop
  // NOTE: When enabled=false, this is manually controlled via start/stop methods
  useEffect(() => {
    if (enabled && isInitialized && !isListening) {
      start();
    }
    // Don't auto-stop when enabled=false - let manual control handle it
  }, [enabled, isInitialized, isListening, start]);

  return {
    isListening,
    isInitialized,
    error,
    start,
    stop,
  };
};
